<template>
  <div
    class="document-field-marker"
    :class="[`signee-${signeeType}`, { completed, disabled }]"
    :style="markerStyle"
    :title="markerTitle"
    @click.stop="handleClick"
  >
    <span class="marker-icon">
      <i v-if="completed" class="fa fa-check"></i>
      <i v-else :class="fieldTypeIcon"></i>
    </span>
    <span class="marker-label">{{ displayLabel }}</span>
  </div>
</template>

<script>
export default {
  name: 'DocumentFieldMarker',
  props: {
    field: { type: Object, required: true },
    scale: { type: Number, default: 1 },
    signeeType: { type: String, default: 'unassigned' },
    completed: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  computed: {
    isSignatureField() {
      return this.field.annotations?.some(a => a.type === 'signature') ?? false;
    },
    hasFilledValue() {
      const v = this.field.value;
      return v !== null && v !== undefined && v !== '';
    },
    fieldLabel() {
      return this.field.label || this.field.field_name || 'Field';
    },
    displayLabel() {
      if (this.completed && !this.isSignatureField && this.hasFilledValue) {
        return String(this.field.value);
      }
      return this.fieldLabel;
    },
    markerStyle() {
      const pos = this.field.position || {};
      const s = this.scale || 1;
      return {
        left: `${(pos.x || 0) * s}px`,
        top: `${(pos.y || 0) * s}px`,
        width: `${(pos.width || 0) * s}px`,
        height: `${(pos.height || 0) * s}px`
      };
    },
    fieldTypeIcon() {
      const icons = {
        signature: 'fa fa-pen-fancy',
        text: 'fa fa-font',
        textarea: 'fa fa-align-left',
        email: 'fa fa-envelope',
        phone: 'fa fa-phone',
        number: 'fa fa-hashtag',
        date: 'fa fa-calendar',
        select: 'fa fa-list',
        dropdown: 'fa fa-caret-down',
        checkbox: 'fa fa-check-square',
        radio: 'fa fa-dot-circle'
      };
      return icons[this.field.field_type] || 'fa fa-edit';
    },
    markerTitle() {
      const label = this.fieldLabel;
      if (this.completed) {
        if (!this.isSignatureField && this.hasFilledValue) {
          return `${label}: ${this.field.value}`;
        }
        return `${label} — completed`;
      }
      if (this.disabled) return `${label} — assigned to ${this.signeeType}`;
      return `${label} — click to fill`;
    }
  },
  methods: {
    handleClick() {
      if (this.disabled) return;
      this.$emit('click', this.field);
    }
  }
};
</script>

<style scoped>
.document-field-marker {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1.5px solid;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: filter 0.15s ease, transform 0.15s ease;
  box-sizing: border-box;
}

.document-field-marker:hover:not(.disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.document-field-marker.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  pointer-events: none;
}

.document-field-marker.completed {
  opacity: 0.85;
}

.marker-icon {
  flex-shrink: 0;
  font-size: 10px;
}

.marker-label {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Role-keyed colors — match DocumentFieldProgress.vue / PendingFieldsPanel.vue */
.signee-student {
  background-color: rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
  color: #1e3a8a;
}
.signee-guardian {
  background-color: rgba(16, 185, 129, 0.18);
  border-color: #10b981;
  color: #065f46;
}
.signee-instructor {
  background-color: rgba(245, 158, 11, 0.18);
  border-color: #f59e0b;
  color: #78350f;
}
.signee-staff {
  background-color: rgba(139, 92, 246, 0.15);
  border-color: #8b5cf6;
  color: #4c1d95;
}
.signee-admin {
  background-color: rgba(239, 68, 68, 0.15);
  border-color: #ef4444;
  color: #7f1d1d;
}
.signee-unassigned,
.signee-null,
.signee-undefined {
  background-color: rgba(107, 114, 128, 0.15);
  border-color: #6b7280;
  color: #1f2937;
}

.document-field-marker.completed {
  background-color: rgba(16, 185, 129, 0.25);
  border-color: #10b981;
  color: #065f46;
}
</style>
